import { Injectable } from '@angular/core';
import { catchError, map, switchMap } from 'rxjs/operators';

import { throwOnGraphqlError } from '@app/core';
import {
  DynamicTemplatesGQL,
  DynamicTemplatesQueryVariables,
  DynamicTemplateGQL,
  DynamicTemplateQueryVariables,
  CreateDynamicTemplateGQL,
  CreateDynamicTemplateMutationVariables,
  UpdateDynamicTemplateGQL,
  UpdateDynamicTemplateMutationVariables,
  DeleteDynamicTemplateGQL,
  DeleteDynamicTemplateMutationVariables,
  DeliveryMethodEnum,
  DynamicTemplateThemeEnum
} from 'src/generated/graphql.default';
import { omit } from 'lodash';
import {
  SignerInput,
  DynamicTemplateVariableInput,
  DynamicTemplateInput,
  DynamicTemplateVariable,
  DynamicTemplate,
  SelectOption,
  CustomSignerInput,
  DynamicTemplateSigner,
  SignerMainAttributes,
  SecurityVerificationType,
  SecurityVerificationEnum,
  ActionEnum,
  DynamicTemplateData,
  DynamicDocumentFieldsTypes,
  DynamicDocumentData,
  FallbackBehaviorEnum
} from '@app/models';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EMPTY, forkJoin, of } from 'rxjs';
import { Editor } from '@tiptap/core';
import { HttpClient } from '@angular/common/http';
import { ErrorHandlerService } from './error-handler.service';

export type OSOption = 'Windows' | 'macOS' | 'Linux' | 'other';

@Injectable({ providedIn: 'root' })
export class DynamicDocumentService {
  constructor(
    private dynamicDocumentsGQL: DynamicTemplatesGQL,
    private dynamicDocumentGQL: DynamicTemplateGQL,
    private createDynamicTemplateGQL: CreateDynamicTemplateGQL,
    private updateDynamicTemplateGQL: UpdateDynamicTemplateGQL,
    private deleteDynamicTemplateGQL: DeleteDynamicTemplateGQL,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService
  ) {}

  allTemplates(variables: DynamicTemplatesQueryVariables) {
    return this.dynamicDocumentsGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.dynamicTemplates)
    );
  }

  template(variables: DynamicTemplateQueryVariables) {
    return this.dynamicDocumentGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.dynamicTemplate)
    );
  }

  create(variables: CreateDynamicTemplateMutationVariables) {
    return this.createDynamicTemplateGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.createDynamicTemplate)
    );
  }

  update(variables: UpdateDynamicTemplateMutationVariables) {
    return this.updateDynamicTemplateGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateDynamicTemplate)
    );
  }

  delete(variables: DeleteDynamicTemplateMutationVariables) {
    return this.deleteDynamicTemplateGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.deleteDynamicTemplate)
    );
  }

  duplicate(id: string, newName: string) {
    return this.template({ id }).pipe(
      switchMap((dynamicTemplate: DynamicTemplate) => {
        return forkJoin({
          dynamicTemplate: of(dynamicTemplate),
          templateFileData: this.getDynamicDocumentData(dynamicTemplate.path)
        });
      }),
      map(({ dynamicTemplate, templateFileData }) => {
        const attrs = { ...dynamicTemplate };
        delete attrs.path;

        const template: DynamicTemplate = {
          ...attrs,

          content: (templateFileData as DynamicTemplateData).template.content || '',
          signers: templateFileData.signers.map(s => {
            s.delivery_method = `DELIVERY_METHOD_${s.delivery_method.toUpperCase()}` as DeliveryMethodEnum;
            (s.security_verifications || []).map(sv => {
              if (!!sv.fallback_behavior) {
                sv.fallback_behavior = sv.fallback_behavior.toUpperCase() as FallbackBehaviorEnum;
              }
              sv.type = sv.type.toUpperCase() as SecurityVerificationEnum;
              return sv;
            });

            return s as SignerInput;
          }),
          variables: templateFileData.variables
        };

        if (!template) return;

        const templateParams: DynamicTemplateInput = omit(template, '__typename', 'sqid', 'variables', 'signers', 'user', 'updated_at', 'created_at') as DynamicTemplateInput;
        templateParams.name = newName;
        templateParams.content = template.content;

        const variablesParams: Array<DynamicTemplateVariableInput> = template.variables.map((variable: DynamicTemplateVariable) => {
          return omit(
            variable,
            'filled_by.defined_signer_config.__typename',
            'filled_by.defined_signer_config.configs.__typename',
            'filled_by.__typename',
            '__typename'
          ) as DynamicTemplateVariableInput;
        });

        const signersParams: Array<SignerInput> = template.signers.map(signer => {
          signer = omit(signer, '__typename', 'configs.__typename', 'security_verifications.__typename');
          signer.security_verifications.forEach((obj: SecurityVerificationType, index: number) => {
            const entries = Object.entries(obj);
            const filteredEntries = entries.filter(([key, value]) => value != null && key != '__typename');
            signer.security_verifications[index] = Object.fromEntries(filteredEntries);
          });

          return signer as SignerInput;
        });

        return {
          template: templateParams,
          signers: signersParams,
          variables: variablesParams
        };
      }),
      switchMap(newTemplate => {
        return this.create(newTemplate);
      }),
      catchError((err: any) => {
        console.error(err);
        return EMPTY;
      })
    );
  }

  getDynamicDocumentData(documentFile: string) {
    return this.http.get<DynamicTemplateData | DynamicDocumentData>(documentFile).pipe(
      map(response => response),
      catchError(error => {
        this.errorHandlerService.handle(error);
        return EMPTY;
      })
    );
  }

  getDynamicDocumentDefaultFields(translateService: any): Array<DynamicTemplateVariable> {
    return [
      {
        name: translateService.instant('documents.fields.fullName'),
        type: DynamicDocumentFieldsTypes.shortTextField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.fullNameText'),
        regex: ''
      },
      {
        name: translateService.instant('documents.fields.cpf'),
        type: DynamicDocumentFieldsTypes.cpfField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.cpfText'),
        regex: ''
      },
      {
        name: translateService.instant('documents.fields.cnpj'),
        type: DynamicDocumentFieldsTypes.cnpjField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.cnpjText'),
        regex: ''
      },
      {
        name: translateService.instant('documents.fields.birthDate'),
        type: DynamicDocumentFieldsTypes.dateField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.birthDateText'),
        regex: ''
      },
      {
        name: translateService.instant('documents.fields.nationality'),
        type: DynamicDocumentFieldsTypes.shortTextField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.nationalityText'),
        regex: ''
      },
      {
        name: translateService.instant('documents.fields.maritalStatus'),
        type: DynamicDocumentFieldsTypes.shortTextField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.maritalStatusText'),
        regex: ''
      },
      {
        name: translateService.instant('documents.fields.address'),
        type: DynamicDocumentFieldsTypes.longTextField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.addressText'),
        regex: ''
      },
      {
        name: translateService.instant('documents.fields.cep'),
        type: DynamicDocumentFieldsTypes.cepField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.cepText'),
        regex: ''
      },
      {
        name: translateService.instant('documents.fields.profession'),
        type: DynamicDocumentFieldsTypes.shortTextField,
        show_help: true,
        required: true,
        help_text: translateService.instant('documents.fields.professionText'),
        regex: ''
      }
    ];
  }

  getDynamicDocumentCategoryOptions(translateService: any): SelectOption[] {
    return [
      {
        key: 'contracts',
        value: translateService.instant('documents.templateOptions.contracts')
      }
    ];
  }

  getDynamicDocumentThemeOptions(translateService: any): SelectOption[] {
    return [
      {
        key: DynamicTemplateThemeEnum.Modern,
        value: translateService.instant('documents.templateOptions.themeModern')
      },
      {
        key: DynamicTemplateThemeEnum.Mixed,
        value: translateService.instant('documents.templateOptions.themeMixed')
      },
      {
        key: DynamicTemplateThemeEnum.Classic,
        value: translateService.instant('documents.templateOptions.themeClassic')
      }
    ];
  }

  getDynamicDocumentBlockOptions(translateService: any): SelectOption[] {
    return [
      {
        key: 'body',
        value: translateService.instant('documents.editor.body'),
        className: 'theme-option-body'
      },
      {
        key: 'h1',
        value: translateService.instant('documents.editor.title', { level: '' }),
        className: 'theme-option-h1'
      },
      {
        key: 'subtitle',
        value: translateService.instant('documents.editor.subtitle'),
        className: 'theme-option-subtitle'
      },
      {
        key: 'h2',
        value: translateService.instant('documents.editor.title', { level: '1' }),
        className: 'theme-option-h2'
      },
      {
        key: 'h3',
        value: translateService.instant('documents.editor.title', { level: '2' }),
        className: 'theme-option-h3'
      },
      {
        key: 'h4',
        value: translateService.instant('documents.editor.title', { level: '3' }),
        className: 'theme-option-h4'
      },
      {
        key: 'blockquote',
        value: translateService.instant('documents.editor.blockquote'),
        className: 'theme-option-blockquote'
      }
    ];
  }

  getDynamicDocumentToolbarColors(): Array<Array<string>> {
    return [
      ['', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff'],
      ['#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff'],
      ['#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff'],
      ['#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2'],
      ['#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466']
    ];
  }

  getDynamicDocumentDefaultAligments(theme: DynamicTemplateThemeEnum): Record<string, string> {
    return {
      h1: 'center',
      h2: 'left',
      h3: 'left',
      h4: 'left',
      subtitle: 'center',
      p: theme === DynamicTemplateThemeEnum.Mixed ? 'left' : 'justify',
      li: theme === DynamicTemplateThemeEnum.Mixed ? 'left' : 'justify',
      blockquote: theme === DynamicTemplateThemeEnum.Mixed ? 'left' : 'justify'
    };
  }

  getDynamicDocumentTableMenuOptions(translateService: any) {
    return {
      operationMenu: {
        items: {
          insertColumnRight: {
            text: translateService.instant('documents.editor.insertColumnRight')
          },
          insertColumnLeft: {
            text: translateService.instant('documents.editor.insertColumnLeft')
          },
          insertRowUp: {
            text: translateService.instant('documents.editor.insertRowUp')
          },
          insertRowDown: {
            text: translateService.instant('documents.editor.insertRowDown')
          },
          mergeCells: {
            text: translateService.instant('documents.editor.mergeCells')
          },
          unmergeCells: {
            text: translateService.instant('documents.editor.unmergeCells')
          },
          deleteColumn: {
            text: translateService.instant('documents.editor.deleteColumn')
          },
          deleteRow: {
            text: translateService.instant('documents.editor.deleteRow')
          },
          deleteTable: {
            text: translateService.instant('documents.editor.deleteTable')
          }
        }
      }
    };
  }

  retrieveSignersData(currentSigners: Array<DynamicTemplateSigner>): Array<CustomSignerInput> {
    let signers: Array<CustomSignerInput> = [];

    function addNewSigner() {
      const mainAttribute = SignerMainAttributes.Email;
      signers.push({
        [mainAttribute]: '',
        action: ActionEnum.Sign,
        configs: {},
        _extra: { signerMainAttribute: mainAttribute, phoneNumber: '', documentType: SecurityVerificationEnum.Manual, fallbackEnabled: true },
        security_verifications: []
      });
    }

    currentSigners.forEach((currentSigner: any, index: number) => {
      if (currentSigner !== undefined) {
        addNewSigner();
        Object.assign(signers[signers.length - 1], currentSigner);

        let _extra: CustomSignerInput['_extra'] = { signerMainAttribute: SignerMainAttributes.Email, isEditable: false };

        if (currentSigner.delivery_method == 'email') {
          _extra.signerMainAttribute = SignerMainAttributes.Email;
        }
        if (currentSigner.delivery_method == 'link') {
          _extra.signerMainAttribute = SignerMainAttributes.Name;
        }
        if (currentSigner.delivery_method == 'sms') {
          _extra.signerMainAttribute = SignerMainAttributes.Sms;

          signers[index].sms = signers[index].phone = currentSigner.phone;
        }
        if (currentSigner.delivery_method == 'whatsapp') {
          _extra.signerMainAttribute = SignerMainAttributes.Whatsapp;

          signers[index].whatsapp = signers[index].phone = currentSigner.phone;
        }

        if (currentSigner.email !== null || currentSigner.phone !== null || currentSigner.name !== null) {
          _extra.isEditable = true;
        }

        currentSigner.security_verifications.map((options: any) => {
          if (options.fallback_behavior?.toUpperCase() === FallbackBehaviorEnum.DisableFallback) {
            _extra.fallbackEnabled = false;
          }

          if (options.type == SecurityVerificationEnum.Sms) {
            _extra.showSmsVerification = true;
            if (options.verify_phone) {
              _extra.phoneNumber = options.verify_phone;
            }
          } else {
            _extra.showSecurityVerifications = true;
            _extra.documentType = options.type.toUpperCase();
          }
          _extra.showOptions = true;
        });

        if (currentSigner.configs.cpf) {
          _extra.showCpfVerification = true;
          _extra.showOptions = true;
        }

        const thisSigner = signers[signers.length - 1];
        Object.assign(thisSigner._extra, _extra);
      }
    });

    if (signers.length == 0) {
      addNewSigner();
    }

    return signers;
  }

  detectOS(): OSOption {
    const userAgent = navigator.userAgent;

    if (/windows nt/i.test(userAgent)) {
      return 'Windows';
    } else if (/macintosh|mac os x/i.test(userAgent)) {
      return 'macOS';
    } else if (/linux/i.test(userAgent)) {
      return 'Linux';
    } else {
      return 'other';
    }
  }

  getTableMenuIcons(): Record<string, SafeHtml> {
    return {
      insertColumnBefore: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.3em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)"d="M4 3h14a2 2 0 0 1 2 2v7.08a6 6 0 0 0-4.32.92H12v4h1.08c-.11.68-.11 1.35 0 2H4a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2m0 4v4h6V7zm8 0v4h6V7zm-8 6v4h6v-4zm14.44 2v2h4v2h-4v2l-3-3z"/></svg>`
      ),
      insertColumnAfter: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.3em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)" d="M4 3h14a2 2 0 0 1 2 2v7.08a6 6 0 0 0-4.32.92H12v4h1.08c-.11.68-.11 1.35 0 2H4a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2m0 4v4h6V7zm8 0v4h6V7zm-8 6v4h6v-4zm15.44 8v-2h-4v-2h4v-2l3 3z"/></svg>`
      ),
      insertRowBefore: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.3em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)" d="M4 3h14a2 2 0 0 1 2 2v7.08a6 6 0 0 0-4.32.92H12v4h1.08c-.11.68-.11 1.35 0 2H4a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2m0 4v4h6V7zm8 0v4h6V7zm-8 6v4h6v-4zm17.94 4.5h-2v4h-2v-4h-2l3-3z"/></svg>`
      ),
      insertRowAfter: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.3em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)" d="M4 3h14a2 2 0 0 1 2 2v7.08a6 6 0 0 0-4.32.92H12v4h1.08c-.11.68-.11 1.35 0 2H4a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2m0 4v4h6V7zm8 0v4h6V7zm-8 6v4h6v-4zm11.94 5.5h2v-4h2v4h2l-3 3z"/></svg>`
      ),

      deleteColumn: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.25em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)" d="M4 2h7a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2m0 8v4h7v-4zm0 6v4h7v-4zM4 4v4h7V4zm13.59 8L15 9.41L16.41 8L19 10.59L21.59 8L23 9.41L20.41 12L23 14.59L21.59 16L19 13.41L16.41 16L15 14.59z"/></svg>`
      ),
      deleteRow: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.4em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)" d="M9.41 13L12 15.59L14.59 13L16 14.41L13.41 17L16 19.59L14.59 21L12 18.41L9.41 21L8 19.59L10.59 17L8 14.41zM22 9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2zM4 9h4V6H4zm6 0h4V6h-4zm6 0h4V6h-4z"/></svg>`
      ),
      deleteTable: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.3em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)" d="m15.46 15.88l1.42-1.42L19 16.59l2.12-2.13l1.42 1.42L20.41 18l2.13 2.12l-1.42 1.42L19 19.41l-2.12 2.13l-1.42-1.42L17.59 18zM4 3h14a2 2 0 0 1 2 2v7.08a6 6 0 0 0-4.32.92H12v4h1.08c-.11.68-.11 1.35 0 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2m0 4v4h6V7zm8 0v4h6V7zm-8 6v4h6v-4z"/></svg>`
      ),

      mergeCells: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.25em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)" d="M5 10H3V4h8v2H5zm14 8h-6v2h8v-6h-2zM5 18v-4H3v6h8v-2zM21 4h-8v2h6v4h2zM8 13v2l3-3l-3-3v2H3v2zm8-2V9l-3 3l3 3v-2h5v-2z"/></svg>`
      ),
      splitCell: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.25em" viewBox="0 0 24 24"><path fill="var(--clr-structure-text-l1)" d="M19 14h2v6H3v-6h2v4h14zM3 4v6h2V6h14v4h2V4zm8 7v2H8v2l-3-3l3-3v2zm5 0V9l3 3l-3 3v-2h-3v-2z"/></svg>`
      ),

      toggleZebraPattern: this.sanitizer.bypassSecurityTrustHtml(
        `<svg width="1.3em" height="1em" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg" fill="var(--clr-structure-text-l1)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M1740 0c99.24 0 180 80.76 180 180v1560c0 99.24-80.76 180-180 180H180c-99.24 0-180-80.76-180-180V180C0 80.76 80.76 0 180 0Zm60 1320h-480v480h420c33 0 60-27 60-60v-420Zm-600 0H720v480h480v-480Zm-600 0H120v420c0 33 27 60 60 60h420v-480ZM1740 120h-420v480h480V180c0-33-27-60-60-60Zm-540 0H720v480h480V120Zm-600 0H180c-33 0-60 27-60 60v420h480V120Z" fill-rule="evenodd"></path></g></svg>`
      )
    };
  }

  getDynamicDocumentSummaryHTML(editor: Editor): HTMLElement {
    if (!editor) return;

    const dom = document.createElement('div');
    dom.className = 'editor-summary';
    dom.contentEditable = 'false';
    let summary: any = [];
    editor.state.doc.descendants(node => {
      if (node.type.name === 'heading' && [2, 3, 4].includes(node.attrs.level) && node.attrs.id) {
        summary.push(node);
      }
    });

    summary.forEach((item: any) => {
      const index = item.attrs.id;
      const anchor = document.createElement('a');

      anchor.className = 'editor-summary__item';

      anchor.href = '#' + index;
      anchor.innerText = item.textContent;

      const level = (index.match(/\./g) || []).length - 1;
      anchor.setAttribute('data-summary-level', level.toString());
      anchor.setAttribute('data-summary-index', index);

      anchor.addEventListener('click', e => {
        e.preventDefault();
        const index = (e.target as HTMLElement).getAttribute('data-summary-index');
        document.querySelector(`[id="${index}"]`).scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
      });

      anchor.addEventListener('dragstart', e => e.preventDefault());

      dom.appendChild(anchor);
    });

    return dom;
  }

  updateEditorNodesAttributes(editor: Editor, nodeType: string, attrs: Record<string, any>) {
    editor.commands.command(({ tr, state }) => {
      const { doc } = state;
      doc.descendants((node, pos) => {
        if (node.type.name === nodeType) {
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            ...attrs
          });
        }
      });

      return true;
    });
  }

  updateInlineFieldAttrs(editor: Editor, element: HTMLElement, newAttr: Record<string, string>) {
    const { view, state } = editor;

    const position = this.getNodePosFromElement(element, view);

    if (position !== null) {
      const node = state.doc.nodeAt(position);

      if (node) {
        const currentAttrs: Record<string, any> = node.attrs;

        if (newAttr['selected'] === 'false') {
          delete currentAttrs.selected;
          newAttr = {};
        }

        const tr = state.tr
          .setNodeMarkup(position, null, {
            ...currentAttrs,
            ...newAttr
          })
          .setMeta('addToHistory', false);

        view.dispatch(tr);
      }
    }
  }

  getNodePosFromElement(element: HTMLElement, view: any) {
    const coords = element.getBoundingClientRect();
    const { left, top } = coords;

    const pos = view.posAtCoords({ left: left + 10, top: top + 10 });

    return pos ? pos.pos : null;
  }

  getDomForDynamicDocumentFile(content: string, theme: DynamicTemplateThemeEnum) {
    let templateMargin: Record<string, string> = { x: '', y: '2.5cm 0' };

    if (!content.match(/@page{/)) {
      if (theme === DynamicTemplateThemeEnum.Classic) {
        templateMargin.x = '0 3cm';
      }
      if (theme === DynamicTemplateThemeEnum.Mixed) {
        templateMargin.x = '0 3cm 0 3.2cm';
      }
      if (theme === DynamicTemplateThemeEnum.Modern) {
        templateMargin.x = '0 2.8cm 0 3.2cm';
      }

      content += `<style>@page{margin: ${templateMargin.y}; size: 210mm 297mm;}</style>`;
    }

    return `<div class="attq-document" style="margin: ${templateMargin.x};">` + this.fixDynamicDocumentContentForFile(content) + '</div>';
  }

  fixDynamicDocumentContentForFile(html: string): string {
    html = this.removeOnlyNumberFieldsFromHTML(html);

    return html.replace(/<table\s*data-column-size="([^"]*)"[^>]*>([\s\S]*?)<\/table>/g, (match, attrValue, tableContent) => {
      const columnSizes = attrValue.split(',').map((v: any) => v.trim() || null);
      const numberOfCols = tableContent.matchAll(/(<col\sstyle="min-width:\s25px;">)/g).reduce((acc: number) => ++acc, 0);

      let index = 0;
      const newTableContent = tableContent.replace(/<col\sstyle="min-width:\s25px;">/g, () => {
        const size = columnSizes[index++] ?? 100 / numberOfCols;
        return `<col style="min-width: 25px; width: ${size}vw"/>`;
      });

      return `<table>${newTableContent}</table>`;
    });
  }

  removeOnlyNumberFieldsFromHTML(html: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || '', 'text/html');

    const spans = doc.querySelectorAll('span.field--inline');

    spans.forEach(span => {
      const onlyNumbersRegex = /^\d+$/;

      const nameAttr = span.getAttribute('name').trim();
      const dataNameAttr = span.getAttribute('data-field-name').trim();
      const textContent = span.textContent.trim();

      const isNameNumeric = nameAttr && onlyNumbersRegex.test(nameAttr);
      const isDataNameNumeric = dataNameAttr && onlyNumbersRegex.test(dataNameAttr);
      const isTextNumeric = textContent && onlyNumbersRegex.test(textContent);

      if (isNameNumeric || isDataNameNumeric || isTextNumeric) {
        span.remove();
      }
    });

    return doc.body.innerHTML;
  }
}
